import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useOutletContext } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { 
  Plane, Hotel, Ticket, Send, Plus, X, Calendar, User, 
  Clock, MapPin, Building, Mail, Image, Upload, FileText,
  ChevronDown, ChevronUp, Trash2, Eye, Users, QrCode, Train, Bus,
  Scan, Loader2, Sparkles, FileUp
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const VOUCHER_TYPES = {
  hotel: {
    label: 'Hotel Voucher',
    icon: Hotel,
    activeClasses: 'border-blue-500 bg-blue-50',
    iconClass: 'text-blue-600',
    titleClass: 'text-blue-700',
    badgeClass: 'bg-blue-100',
    fields: [
      { name: 'hotel_name', label: 'Hotel Name', type: 'text', required: true },
      { name: 'hotel_address', label: 'Hotel Address', type: 'textarea' },
      { name: 'hotel_maps_url', label: 'Google Maps URL (Optional)', type: 'text', placeholder: 'Auto-generated from address if blank' },
      { name: 'check_in', label: 'Check-in Date', type: 'date', required: true },
      { name: 'check_out', label: 'Check-out Date', type: 'date', required: true },
      { name: 'check_in_time', label: 'Check-in Time', type: 'time' },
      { name: 'check_out_time', label: 'Check-out Time', type: 'time' },
      { name: 'room_type', label: 'Room Type', type: 'text' },
      { name: 'num_rooms', label: 'Number of Rooms', type: 'number' },
      { name: 'num_adults', label: 'Number of Adults', type: 'number', placeholder: '1' },
      { name: 'num_kids', label: 'Number of Children', type: 'number', placeholder: '0' },
      { name: 'meal_plan', label: 'Meal Plan', type: 'select', options: ['Room Only', 'Breakfast Included', 'Half Board', 'Full Board', 'All Inclusive'] },
      { name: 'booking_ref', label: 'Booking Reference', type: 'text' },
      { name: 'special_requests', label: 'Special Requests', type: 'textarea' },
      { name: 'total_amount', label: 'Total Amount', type: 'text' },
      { name: 'payment_status', label: 'Payment Status', type: 'select', options: ['Paid', 'Partially Paid', 'Pay at Hotel'] },
    ]
  },
  flight: {
    label: 'Flight Voucher',
    icon: Plane,
    activeClasses: 'border-indigo-500 bg-indigo-50',
    iconClass: 'text-indigo-600',
    titleClass: 'text-indigo-700',
    badgeClass: 'bg-indigo-100',
    hasSegments: true, // Flag for multi-segment support
    commonFields: [
      { name: 'pnr', label: 'PNR / Booking Reference', type: 'text', required: true },
      { name: 'airline', label: 'Airline', type: 'text', required: true },
      { name: 'cabin_class', label: 'Cabin Class', type: 'select', options: ['Economy', 'Economy Lite', 'Premium Economy', 'Business', 'First Class'] },
      { name: 'baggage_allowance', label: 'Baggage Allowance', type: 'text', placeholder: 'e.g., 15kg Check-in + 7kg Cabin' },
    ],
    segmentFields: [
      { name: 'flight_number', label: 'Flight Number', type: 'text', required: true },
      { name: 'departure_city', label: 'Departure City', type: 'text', required: true },
      { name: 'departure_airport', label: 'Departure Airport', type: 'text', placeholder: 'e.g., Chhatrapati Shivaji Intl (BOM)' },
      { name: 'arrival_city', label: 'Arrival City', type: 'text', required: true },
      { name: 'arrival_airport', label: 'Arrival Airport', type: 'text', placeholder: 'e.g., Noi Bai Intl (HAN)' },
      { name: 'departure_date', label: 'Departure Date', type: 'date', required: true },
      { name: 'departure_time', label: 'Departure Time', type: 'time', required: true },
      { name: 'arrival_date', label: 'Arrival Date', type: 'date' },
      { name: 'arrival_time', label: 'Arrival Time', type: 'time', required: true },
      { name: 'duration', label: 'Flight Duration', type: 'text', placeholder: 'e.g., 4h 45m' },
    ],
    fields: [
      { name: 'pnr', label: 'PNR / Booking Reference', type: 'text', required: true },
      { name: 'airline', label: 'Airline', type: 'text', required: true },
      { name: 'flight_number', label: 'Flight Number', type: 'text', required: true },
      { name: 'departure_city', label: 'Departure City', type: 'text', required: true },
      { name: 'departure_airport', label: 'Departure Airport', type: 'text' },
      { name: 'arrival_city', label: 'Arrival City', type: 'text', required: true },
      { name: 'arrival_airport', label: 'Arrival Airport', type: 'text' },
      { name: 'departure_date', label: 'Departure Date', type: 'date', required: true },
      { name: 'departure_time', label: 'Departure Time', type: 'time', required: true },
      { name: 'arrival_date', label: 'Arrival Date', type: 'date' },
      { name: 'arrival_time', label: 'Arrival Time', type: 'time', required: true },
      { name: 'duration', label: 'Flight Duration', type: 'text' },
      { name: 'stops', label: 'Stops', type: 'text', placeholder: 'e.g., Non-stop or 1 Stop via Delhi' },
      { name: 'cabin_class', label: 'Cabin Class', type: 'select', options: ['Economy', 'Economy Lite', 'Premium Economy', 'Business', 'First Class'] },
      { name: 'baggage_allowance', label: 'Baggage Allowance', type: 'text', placeholder: 'e.g., 15kg Check-in + 7kg Cabin' },
    ]
  },
  train: {
    label: 'Train Ticket',
    icon: Train,
    activeClasses: 'border-rose-500 bg-rose-50',
    iconClass: 'text-rose-600',
    titleClass: 'text-rose-700',
    badgeClass: 'bg-rose-100',
    fields: [
      { name: 'pnr', label: 'PNR Number', type: 'text', required: true },
      { name: 'train_name', label: 'Train Name', type: 'text', required: true },
      { name: 'train_number', label: 'Train Number', type: 'text', required: true },
      { name: 'departure_station', label: 'Departure Station', type: 'text', required: true },
      { name: 'arrival_station', label: 'Arrival Station', type: 'text', required: true },
      { name: 'departure_date', label: 'Departure Date', type: 'date', required: true },
      { name: 'departure_time', label: 'Departure Time', type: 'time', required: true },
      { name: 'arrival_time', label: 'Arrival Time', type: 'time' },
      { name: 'duration', label: 'Journey Duration', type: 'text', placeholder: 'e.g., 12h 30m' },
      { name: 'travel_class', label: 'Class', type: 'select', options: ['1A (First AC)', '2A (2-Tier AC)', '3A (3-Tier AC)', 'SL (Sleeper)', 'CC (Chair Car)', '2S (Second Sitting)'] },
      { name: 'coach_seat', label: 'Coach / Seat / Berth', type: 'text', placeholder: 'e.g., B2 / 45 / Lower' },
      { name: 'total_amount', label: 'Total Fare', type: 'text' },
    ]
  },
  bus: {
    label: 'Bus Ticket',
    icon: Bus,
    activeClasses: 'border-cyan-500 bg-cyan-50',
    iconClass: 'text-cyan-600',
    titleClass: 'text-cyan-700',
    badgeClass: 'bg-cyan-100',
    fields: [
      { name: 'booking_id', label: 'Booking ID', type: 'text', required: true },
      { name: 'operator_name', label: 'Bus Operator', type: 'text', required: true },
      { name: 'bus_type', label: 'Bus Type', type: 'select', options: ['Sleeper', 'Semi-Sleeper', 'Seater', 'AC Sleeper', 'AC Semi-Sleeper', 'Volvo Multi-Axle'] },
      { name: 'departure_point', label: 'Departure Point/City', type: 'text', required: true },
      { name: 'arrival_point', label: 'Arrival Point/City', type: 'text', required: true },
      { name: 'departure_date', label: 'Departure Date', type: 'date', required: true },
      { name: 'departure_time', label: 'Departure Time', type: 'time', required: true },
      { name: 'arrival_time', label: 'Arrival Time (Expected)', type: 'time' },
      { name: 'duration', label: 'Journey Duration', type: 'text', placeholder: 'e.g., 8h 30m' },
      { name: 'seat_number', label: 'Seat Number(s)', type: 'text', placeholder: 'e.g., L1, L2' },
      { name: 'boarding_point', label: 'Boarding Point', type: 'text' },
      { name: 'dropping_point', label: 'Dropping Point', type: 'text' },
      { name: 'total_amount', label: 'Total Fare', type: 'text' },
    ]
  },
  activity: {
    label: 'Activity Voucher',
    icon: Ticket,
    activeClasses: 'border-green-500 bg-green-50',
    iconClass: 'text-green-600',
    titleClass: 'text-green-700',
    badgeClass: 'bg-green-100',
    fields: [
      { name: 'activity_name', label: 'Activity/Tour Name', type: 'text', required: true },
      { name: 'activity_date', label: 'Activity Date', type: 'date', required: true },
      { name: 'activity_time', label: 'Activity Time', type: 'time' },
      { name: 'duration', label: 'Duration', type: 'text' },
      { name: 'meeting_point', label: 'Meeting Point / Pickup Location', type: 'textarea' },
      { name: 'inclusions', label: 'Inclusions', type: 'textarea', placeholder: 'e.g., Transport, Guide, Lunch, Entry tickets' },
      { name: 'exclusions', label: 'Exclusions', type: 'textarea' },
      { name: 'booking_ref', label: 'Booking Reference', type: 'text' },
      { name: 'operator_name', label: 'Tour Operator Name', type: 'text' },
      { name: 'operator_contact', label: 'Operator Contact', type: 'text' },
      { name: 'special_instructions', label: 'Special Instructions', type: 'textarea' },
      { name: 'total_amount', label: 'Total Amount', type: 'text' },
    ]
  }
};

const AdminVouchers = () => {
  const { adminAuth } = useOutletContext();
  const [voucherType, setVoucherType] = useState('hotel');
  const [formData, setFormData] = useState({});
  const [images, setImages] = useState([]); // Array of { file, preview, type, label }
  const [sendTo, setSendTo] = useState('');
  const [inquiryId, setInquiryId] = useState('');
  const [sending, setSending] = useState(false);
  const [inquiries, setInquiries] = useState([]);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [recentVouchers, setRecentVouchers] = useState([]);
  
  // New states for multi-passenger support
  const [passengers, setPassengers] = useState([{ name: '', barcode: null }]);
  const [sameBarcode, setSameBarcode] = useState(true);
  const [sharedBarcode, setSharedBarcode] = useState(null);
  
  // Flight segments for connecting flights
  const [flightSegments, setFlightSegments] = useState([{
    flight_number: '',
    departure_city: '',
    departure_airport: '',
    arrival_city: '',
    arrival_airport: '',
    departure_date: '',
    departure_time: '',
    arrival_date: '',
    arrival_time: '',
    duration: ''
  }]);
  
  // Price visibility toggle
  const [showPrice, setShowPrice] = useState(true);
  
  // Voucher scanner states
  const [scanning, setScanning] = useState(false);
  const [showScanModal, setShowScanModal] = useState(false);
  const [scanFile, setScanFile] = useState(null);
  const [scanPreview, setScanPreview] = useState(null);
  const scanInputRef = useRef(null);

  // Fetch inquiries for dropdown
  const fetchInquiries = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/inquiries`, {
        headers: { 'Authorization': `Basic ${adminAuth}` }
      });
      if (res.ok) {
        const data = await res.json();
        setInquiries(data.slice(0, 50));
      }
    } catch (err) {
      console.error('Failed to fetch inquiries', err);
    }
  }, [adminAuth]);

  // Fetch recent vouchers
  const fetchRecentVouchers = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/vouchers`, {
        headers: { 'Authorization': `Basic ${adminAuth}` }
      });
      if (res.ok) {
        const data = await res.json();
        setRecentVouchers(data);
      }
    } catch (err) {
      console.error('Failed to fetch vouchers', err);
    }
  }, [adminAuth]);

  useEffect(() => {
    fetchInquiries();
    fetchRecentVouchers();
  }, [fetchInquiries, fetchRecentVouchers]);

  // Reset form when voucher type changes
  useEffect(() => {
    setFormData({});
    setImages([]);
    setPassengers([{ name: '', barcode: null }]);
    setSameBarcode(true);
    setSharedBarcode(null);
    // Reset flight segments
    setFlightSegments([{
      flight_number: '',
      departure_city: '',
      departure_airport: '',
      arrival_city: '',
      arrival_airport: '',
      departure_date: '',
      departure_time: '',
      arrival_date: '',
      arrival_time: '',
      duration: ''
    }]);
  }, [voucherType]);

  const handleFieldChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // Flight segment management functions
  const handleSegmentChange = (index, field, value) => {
    setFlightSegments(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const addFlightSegment = () => {
    // Pre-fill departure from previous segment's arrival
    const lastSegment = flightSegments[flightSegments.length - 1];
    setFlightSegments(prev => [...prev, {
      flight_number: '',
      departure_city: lastSegment?.arrival_city || '',
      departure_airport: lastSegment?.arrival_airport || '',
      arrival_city: '',
      arrival_airport: '',
      departure_date: lastSegment?.arrival_date || lastSegment?.departure_date || '',
      departure_time: '',
      arrival_date: '',
      arrival_time: '',
      duration: ''
    }]);
  };

  const removeFlightSegment = (index) => {
    if (flightSegments.length > 1) {
      setFlightSegments(prev => prev.filter((_, i) => i !== index));
    }
  };

  // Calculate layover time between segments
  const calculateLayover = (prevSegment, currentSegment) => {
    if (!prevSegment?.arrival_time || !currentSegment?.departure_time) return null;
    
    try {
      const prevArrivalDate = prevSegment.arrival_date || prevSegment.departure_date;
      const currDepartureDate = currentSegment.departure_date;
      
      const arrival = new Date(`${prevArrivalDate}T${prevSegment.arrival_time}`);
      const departure = new Date(`${currDepartureDate}T${currentSegment.departure_time}`);
      
      const diffMs = departure - arrival;
      if (diffMs < 0) return null;
      
      const hours = Math.floor(diffMs / (1000 * 60 * 60));
      const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
      
      return `${hours}h ${minutes}m`;
    } catch {
      return null;
    }
  };

  // Handle scan file selection
  const handleScanFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setScanFile(file);
      // Create preview for images
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onloadend = () => setScanPreview(reader.result);
        reader.readAsDataURL(file);
      } else {
        setScanPreview(null); // PDF - no preview
      }
    }
  };

  // Map extracted data to form fields based on voucher type
  const mapExtractedDataToForm = (data, detectedType) => {
    const newFormData = {};
    const newPassengers = [];

    if (detectedType === 'hotel') {
      newFormData.hotel_name = data.hotel_name || '';
      newFormData.hotel_address = data.hotel_address || '';
      newFormData.check_in = data.check_in_date ? convertDateFormat(data.check_in_date) : '';
      newFormData.check_out = data.check_out_date ? convertDateFormat(data.check_out_date) : '';
      newFormData.check_in_time = data.check_in_time || '';
      newFormData.check_out_time = data.check_out_time || '';
      newFormData.room_type = data.room_type || '';
      newFormData.num_rooms = data.room_count || '';
      newFormData.meal_plan = mapMealPlan(data.meal_plan);
      newFormData.booking_ref = data.booking_id || '';
      newFormData.special_requests = data.special_requests || '';
      newFormData.total_amount = data.total_amount || '';
      
      if (data.passengers && data.passengers.length > 0) {
        data.passengers.forEach(p => {
          newPassengers.push({ name: p.name || '', barcode: null });
        });
      } else if (data.guest_name) {
        newPassengers.push({ name: data.guest_name, barcode: null });
      }
    } 
    else if (detectedType === 'flight') {
      newFormData.pnr = data.pnr || '';
      newFormData.airline = data.airline || '';
      newFormData.cabin_class = data.cabin_class || data.class || 'Economy';
      newFormData.baggage_allowance = data.baggage_allowance || data.baggage || '';
      newFormData.total_amount = data.total_amount || '';
      
      // Handle flight segments from AI Scanner
      if (data.flight_segments && data.flight_segments.length > 0) {
        // AI detected multiple segments (connecting flight)
        const extractedSegments = data.flight_segments.map(seg => ({
          flight_number: seg.flight_number || '',
          departure_city: seg.departure_city || '',
          departure_airport: seg.departure_airport || '',
          arrival_city: seg.arrival_city || '',
          arrival_airport: seg.arrival_airport || '',
          departure_date: seg.departure_date ? convertDateFormat(seg.departure_date) : '',
          departure_time: seg.departure_time || '',
          arrival_date: seg.arrival_date ? convertDateFormat(seg.arrival_date) : '',
          arrival_time: seg.arrival_time || '',
          duration: seg.duration || ''
        }));
        
        // Store segments to be set later
        newFormData._flight_segments = extractedSegments;
        
        // Also set backwards-compatible single flight data from first/last segment
        const firstSeg = extractedSegments[0];
        const lastSeg = extractedSegments[extractedSegments.length - 1];
        newFormData.flight_number = firstSeg.flight_number;
        newFormData.departure_city = firstSeg.departure_city;
        newFormData.departure_airport = firstSeg.departure_airport;
        newFormData.departure_date = firstSeg.departure_date;
        newFormData.departure_time = firstSeg.departure_time;
        newFormData.arrival_city = lastSeg.arrival_city;
        newFormData.arrival_airport = lastSeg.arrival_airport;
        newFormData.arrival_date = lastSeg.arrival_date;
        newFormData.arrival_time = lastSeg.arrival_time;
      } else {
        // Single flight (old format or direct flight)
        newFormData.flight_number = data.flight_number || '';
        newFormData.departure_city = data.departure_city || '';
        newFormData.departure_airport = data.departure_airport || '';
        newFormData.arrival_city = data.arrival_city || '';
        newFormData.arrival_airport = data.arrival_airport || '';
        newFormData.departure_date = data.departure_date ? convertDateFormat(data.departure_date) : '';
        newFormData.departure_time = data.departure_time || '';
        newFormData.arrival_date = data.arrival_date ? convertDateFormat(data.arrival_date) : '';
        newFormData.arrival_time = data.arrival_time || '';
        
        // Create single segment
        newFormData._flight_segments = [{
          flight_number: newFormData.flight_number,
          departure_city: newFormData.departure_city,
          departure_airport: newFormData.departure_airport,
          arrival_city: newFormData.arrival_city,
          arrival_airport: newFormData.arrival_airport,
          departure_date: newFormData.departure_date,
          departure_time: newFormData.departure_time,
          arrival_date: newFormData.arrival_date,
          arrival_time: newFormData.arrival_time,
          duration: data.duration || ''
        }];
      }
      
      if (data.passengers && data.passengers.length > 0) {
        data.passengers.forEach(p => {
          newPassengers.push({ name: p.name || '', ticket_number: p.ticket_number || '', barcode: null });
        });
      } else if (data.passenger_name) {
        newPassengers.push({ name: data.passenger_name, barcode: null });
      }
    }
    else if (detectedType === 'train') {
      newFormData.pnr = data.pnr || '';
      newFormData.train_name = data.train_name || '';
      newFormData.train_number = data.train_number || '';
      newFormData.departure_station = data.from_station || '';
      newFormData.arrival_station = data.to_station || '';
      newFormData.departure_date = data.departure_date ? convertDateFormat(data.departure_date) : '';
      newFormData.departure_time = data.departure_time || '';
      newFormData.arrival_time = data.arrival_time || '';
      newFormData.coach = data.coach || '';
      newFormData.class = data.class || '';
      
      if (data.passengers && data.passengers.length > 0) {
        data.passengers.forEach(p => {
          newPassengers.push({ 
            name: p.name || '', 
            seat: p.seat || '',
            barcode: null 
          });
        });
      } else if (data.passenger_name) {
        newPassengers.push({ name: data.passenger_name, seat: data.seat || '', barcode: null });
      }
    }
    else if (detectedType === 'bus') {
      newFormData.booking_ref = data.booking_id || '';
      newFormData.operator = data.operator || '';
      newFormData.bus_type = data.bus_type || '';
      newFormData.departure_city = data.from_city || '';
      newFormData.arrival_city = data.to_city || '';
      newFormData.boarding_point = data.boarding_point || '';
      newFormData.dropping_point = data.dropping_point || '';
      newFormData.departure_date = data.departure_date ? convertDateFormat(data.departure_date) : '';
      newFormData.departure_time = data.departure_time || '';
      newFormData.arrival_time = data.arrival_time || '';
      newFormData.seat_numbers = data.seat_numbers || '';
      
      if (data.passengers && data.passengers.length > 0) {
        data.passengers.forEach(p => {
          newPassengers.push({ name: p.name || '', barcode: null });
        });
      } else if (data.passenger_name) {
        newPassengers.push({ name: data.passenger_name, barcode: null });
      }
    }
    else if (detectedType === 'activity') {
      newFormData.activity_name = data.activity_name || '';
      newFormData.activity_type = data.activity_type || '';
      newFormData.provider = data.provider || '';
      newFormData.location = data.location || '';
      newFormData.date = data.date ? convertDateFormat(data.date) : '';
      newFormData.time = data.time || '';
      newFormData.duration = data.duration || '';
      newFormData.booking_ref = data.booking_id || '';
      newFormData.pickup_location = data.pickup_location || '';
      newFormData.inclusions = data.inclusions || '';
      newFormData.special_instructions = data.special_instructions || '';
      newFormData.total_amount = data.total_amount || '';
      
      if (data.passengers && data.passengers.length > 0) {
        data.passengers.forEach(p => {
          newPassengers.push({ name: p.name || '', barcode: null });
        });
      } else if (data.guest_name) {
        newPassengers.push({ name: data.guest_name, barcode: null });
      }
    }

    return { newFormData, newPassengers: newPassengers.length > 0 ? newPassengers : [{ name: '', barcode: null }] };
  };

  // Convert various date formats to YYYY-MM-DD for date input
  const convertDateFormat = (dateStr) => {
    if (!dateStr) return '';
    
    // Already in YYYY-MM-DD format
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
      return dateStr;
    }
    
    // Try DD/MM/YYYY format
    const slashParts = dateStr.split('/');
    if (slashParts.length === 3) {
      const [day, month, year] = slashParts;
      return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
    }
    
    // Try DD-MM-YYYY format
    const dashParts = dateStr.split('-');
    if (dashParts.length === 3 && dashParts[0].length <= 2) {
      const [day, month, year] = dashParts;
      return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
    }
    
    // Try parsing natural language dates like "25 Dec 2024" or "December 25, 2024"
    try {
      const parsed = new Date(dateStr);
      if (!isNaN(parsed.getTime())) {
        const year = parsed.getFullYear();
        const month = String(parsed.getMonth() + 1).padStart(2, '0');
        const day = String(parsed.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      }
    } catch (e) {
      // Parsing failed
    }
    
    return dateStr;
  };

  // Map meal plan codes to dropdown options
  const mapMealPlan = (plan) => {
    if (!plan) return 'Room Only';
    const planLower = plan.toLowerCase();
    if (planLower.includes('all inclusive') || planLower === 'ai') return 'All Inclusive';
    if (planLower.includes('full board') || planLower === 'fb' || planLower === 'ap') return 'Full Board';
    if (planLower.includes('half board') || planLower === 'hb' || planLower === 'map') return 'Half Board';
    if (planLower.includes('breakfast') || planLower === 'bb' || planLower === 'cp') return 'Breakfast Included';
    return 'Room Only';
  };

  // Scan voucher with AI
  const scanVoucher = async () => {
    if (!scanFile) {
      toast.error('Please select a file to scan');
      return;
    }

    setScanning(true);
    try {
      const formData = new FormData();
      formData.append('file', scanFile);
      formData.append('voucher_type', voucherType);

      const res = await fetch(`${API_URL}/api/admin/vouchers/scan`, {
        method: 'POST',
        headers: { 'Authorization': `Basic ${adminAuth}` },
        body: formData
      });

      if (res.ok) {
        const result = await res.json();
        
        if (result.success && result.extracted_data) {
          // Switch to detected voucher type if different
          const detectedType = result.detected_type || voucherType;
          if (detectedType !== voucherType) {
            setVoucherType(detectedType);
            toast.info(`Detected voucher type: ${detectedType.toUpperCase()}`);
          }
          
          // Map and set form data
          const { newFormData, newPassengers } = mapExtractedDataToForm(result.extracted_data, detectedType);
          
          // Use setTimeout to ensure voucher type state has updated
          setTimeout(() => {
            setFormData(newFormData);
            setPassengers(newPassengers);
            
            // If flight segments were extracted, set them
            if (newFormData._flight_segments && newFormData._flight_segments.length > 0) {
              setFlightSegments(newFormData._flight_segments);
              if (newFormData._flight_segments.length > 1) {
                toast.info(`Detected ${newFormData._flight_segments.length} flight segments (connecting flight)`);
              }
            }
          }, 100);
          
          toast.success('Voucher scanned successfully! Review the extracted data below.');
          setShowScanModal(false);
          setScanFile(null);
          setScanPreview(null);
        } else {
          toast.error('Failed to extract data from voucher');
        }
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to scan voucher');
      }
    } catch (err) {
      console.error('Scan error:', err);
      toast.error('Failed to scan voucher. Please try again.');
    } finally {
      setScanning(false);
    }
  };

  // Handle image upload
  const handleImageUpload = (e, imageType, label = '') => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setImages(prev => [...prev, { 
          file, 
          preview: reader.result, 
          type: imageType,
          label: label || imageType,
          name: file.name 
        }]);
      };
      reader.readAsDataURL(file);
    }
    e.target.value = ''; // Reset input
  };

  const removeImage = (index) => {
    setImages(prev => prev.filter((_, i) => i !== index));
  };

  // Passenger management
  const addPassenger = () => {
    setPassengers(prev => [...prev, { name: '', barcode: null }]);
  };

  const removePassenger = (index) => {
    if (passengers.length > 1) {
      setPassengers(prev => prev.filter((_, i) => i !== index));
    }
  };

  const updatePassenger = (index, field, value) => {
    setPassengers(prev => prev.map((p, i) => 
      i === index ? { ...p, [field]: value } : p
    ));
  };

  const handlePassengerBarcodeUpload = (e, index) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        updatePassenger(index, 'barcode', reader.result);
      };
      reader.readAsDataURL(file);
    }
    e.target.value = '';
  };

  const handleSharedBarcodeUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setSharedBarcode(reader.result);
      };
      reader.readAsDataURL(file);
    }
    e.target.value = '';
  };

  // Validate required fields helper
  const validateFields = () => {
    const typeConfig = VOUCHER_TYPES[voucherType];
    const missingFields = [];

    if (typeConfig.hasSegments) {
      // Flight: common fields live in formData, per-segment fields live in flightSegments
      typeConfig.commonFields
        .filter(f => f.required && !formData[f.name])
        .forEach(f => missingFields.push(f.label));
      flightSegments.forEach((seg, idx) => {
        typeConfig.segmentFields
          .filter(f => f.required && !seg[f.name])
          .forEach(f => missingFields.push(flightSegments.length > 1 ? `${f.label} (Flight ${idx + 1})` : f.label));
      });
    } else {
      typeConfig.fields
        .filter(f => f.required && !formData[f.name])
        .forEach(f => missingFields.push(f.label));
    }
    
    // Check passenger names
    const hasEmptyPassenger = passengers.some(p => !p.name.trim());
    if (hasEmptyPassenger) {
      missingFields.push('All passenger/guest names');
    }
    
    if (missingFields.length > 0) {
      toast.error(`Missing required fields: ${missingFields.join(', ')}`);
      return false;
    }
    return true;
  };

  // Generate voucher PDF
  const generateVoucherPDF = async (action = 'preview') => {
    if (!validateFields()) return;

    setGenerating(true);
    try {
      const imageData = images.map(img => ({
        data: img.preview,
        type: img.type,
        label: img.label,
        name: img.name
      }));

      // Build passenger data with barcodes
      const passengerData = passengers.map((p, idx) => ({
        name: p.name,
        ticket_number: p.ticket_number || '',
        barcode: sameBarcode ? sharedBarcode : p.barcode,
        index: idx + 1
      }));

      // Build form data - include flight segments for flight vouchers
      let submitFormData = { ...formData, show_price: showPrice };
      if (voucherType === 'flight' && flightSegments.length > 0) {
        submitFormData.flight_segments = flightSegments;
        // For backwards compatibility, also set first segment data as main flight data
        const firstSeg = flightSegments[0];
        submitFormData.flight_number = firstSeg.flight_number;
        submitFormData.departure_city = firstSeg.departure_city;
        submitFormData.departure_airport = firstSeg.departure_airport;
        submitFormData.arrival_city = flightSegments[flightSegments.length - 1].arrival_city;
        submitFormData.arrival_airport = flightSegments[flightSegments.length - 1].arrival_airport;
        submitFormData.departure_date = firstSeg.departure_date;
        submitFormData.departure_time = firstSeg.departure_time;
        submitFormData.arrival_date = flightSegments[flightSegments.length - 1].arrival_date || flightSegments[flightSegments.length - 1].departure_date;
        submitFormData.arrival_time = flightSegments[flightSegments.length - 1].arrival_time;
        // Set stops based on segments
        if (flightSegments.length > 1) {
          const layoverCities = flightSegments.slice(0, -1).map(s => s.arrival_city).filter(Boolean);
          submitFormData.stops = `${flightSegments.length - 1} Stop${flightSegments.length > 2 ? 's' : ''} via ${layoverCities.join(', ')}`;
        } else {
          submitFormData.stops = 'Non-stop';
        }
      }

      const res = await fetch(`${API_URL}/api/admin/vouchers/generate`, {
        method: 'POST',
        headers: {
          'Authorization': `Basic ${adminAuth}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          voucher_type: voucherType,
          form_data: submitFormData,
          images: imageData,
          passengers: passengerData,
          same_barcode: sameBarcode
        })
      });

      if (res.ok) {
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        
        if (action === 'preview') {
          setPreviewUrl(url);
        } else if (action === 'download') {
          const link = document.createElement('a');
          link.href = url;
          const firstName = passengers[0]?.name || 'voucher';
          link.download = `${voucherType}_voucher_${firstName.replace(/\s+/g, '_')}.pdf`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(url);
          toast.success('Voucher downloaded!');
        }
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to generate voucher');
      }
    } catch (err) {
      toast.error('Connection error');
    }
    setGenerating(false);
  };

  const closePreview = () => {
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
    }
  };

  const handleSendVoucher = async () => {
    if (!sendTo.trim()) {
      toast.error('Please enter an email address');
      return;
    }

    if (!validateFields()) return;

    setSending(true);
    try {
      const imageData = images.map(img => ({
        data: img.preview,
        type: img.type,
        label: img.label,
        name: img.name
      }));

      const passengerData = passengers.map((p, idx) => ({
        name: p.name,
        ticket_number: p.ticket_number || '',
        barcode: sameBarcode ? sharedBarcode : p.barcode,
        index: idx + 1
      }));

      // Build form data - include flight segments for flight vouchers
      let submitFormData = { ...formData, show_price: showPrice };
      if (voucherType === 'flight' && flightSegments.length > 0) {
        submitFormData.flight_segments = flightSegments;
        const firstSeg = flightSegments[0];
        submitFormData.flight_number = firstSeg.flight_number;
        submitFormData.departure_city = firstSeg.departure_city;
        submitFormData.departure_airport = firstSeg.departure_airport;
        submitFormData.arrival_city = flightSegments[flightSegments.length - 1].arrival_city;
        submitFormData.arrival_airport = flightSegments[flightSegments.length - 1].arrival_airport;
        submitFormData.departure_date = firstSeg.departure_date;
        submitFormData.departure_time = firstSeg.departure_time;
        submitFormData.arrival_date = flightSegments[flightSegments.length - 1].arrival_date || flightSegments[flightSegments.length - 1].departure_date;
        submitFormData.arrival_time = flightSegments[flightSegments.length - 1].arrival_time;
        if (flightSegments.length > 1) {
          const layoverCities = flightSegments.slice(0, -1).map(s => s.arrival_city).filter(Boolean);
          submitFormData.stops = `${flightSegments.length - 1} Stop${flightSegments.length > 2 ? 's' : ''} via ${layoverCities.join(', ')}`;
        } else {
          submitFormData.stops = 'Non-stop';
        }
      }

      const res = await fetch(`${API_URL}/api/admin/vouchers/send`, {
        method: 'POST',
        headers: {
          'Authorization': `Basic ${adminAuth}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          voucher_type: voucherType,
          form_data: submitFormData,
          images: imageData,
          passengers: passengerData,
          same_barcode: sameBarcode,
          send_to: sendTo.trim(),
          inquiry_id: inquiryId || null
        })
      });

      if (res.ok) {
        toast.success('Voucher sent successfully!');
        setFormData({});
        setImages([]);
        setSendTo('');
        setInquiryId('');
        setPassengers([{ name: '', barcode: null }]);
        setSameBarcode(true);
        setSharedBarcode(null);
        setFlightSegments([{
          flight_number: '',
          departure_city: '',
          departure_airport: '',
          arrival_city: '',
          arrival_airport: '',
          departure_date: '',
          departure_time: '',
          arrival_date: '',
          arrival_time: '',
          duration: ''
        }]);
        fetchRecentVouchers();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to send voucher');
      }
    } catch (err) {
      toast.error('Connection error');
    }
    setSending(false);
  };

  const typeConfig = VOUCHER_TYPES[voucherType];
  const TypeIcon = typeConfig.icon;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Booking Vouchers</h1>
          <p className="text-gray-500">Create and send hotel, flight, and activity vouchers</p>
        </div>
      </div>

      {/* Voucher Type Selector */}
      <div className="grid grid-cols-3 gap-4">
        {Object.entries(VOUCHER_TYPES).map(([type, config]) => {
          const Icon = config.icon;
          const isActive = voucherType === type;
          return (
            <button
              key={type}
              onClick={() => setVoucherType(type)}
              data-testid={`voucher-type-${type}`}
              className={`p-4 rounded-xl border-2 transition-all ${
                isActive ? config.activeClasses : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <Icon className={`w-8 h-8 mx-auto mb-2 ${isActive ? config.iconClass : 'text-gray-400'}`} />
              <div className={`font-semibold ${isActive ? config.titleClass : 'text-gray-600'}`}>
                {config.label}
              </div>
            </button>
          );
        })}
      </div>

      {/* AI Voucher Scanner */}
      <Card className="border-2 border-dashed border-purple-300 bg-gradient-to-r from-purple-50 to-indigo-50">
        <CardContent className="p-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-purple-100 rounded-xl">
                <Sparkles className="w-8 h-8 text-purple-600" />
              </div>
              <div>
                <h3 className="font-semibold text-purple-900 text-lg">AI Voucher Scanner</h3>
                <p className="text-purple-600 text-sm">Upload an existing voucher and let AI extract all the details automatically</p>
              </div>
            </div>
            <Button 
              onClick={() => setShowScanModal(true)}
              className="bg-purple-600 hover:bg-purple-700 text-white"
              data-testid="scan-voucher-btn"
            >
              <Scan className="w-4 h-4 mr-2" />
              Scan Voucher
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Scan Modal */}
      {showScanModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-lg">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-purple-100 rounded-lg">
                    <Scan className="w-6 h-6 text-purple-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg">Scan Voucher</h3>
                    <p className="text-sm text-gray-500">Upload image or PDF of original voucher</p>
                  </div>
                </div>
                <button 
                  onClick={() => { setShowScanModal(false); setScanFile(null); setScanPreview(null); }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              {/* File Upload Area */}
              <div 
                className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-colors ${
                  scanFile ? 'border-purple-400 bg-purple-50' : 'border-gray-300 hover:border-purple-400 hover:bg-purple-50'
                }`}
                onClick={() => scanInputRef.current?.click()}
              >
                <input
                  ref={scanInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp,application/pdf"
                  onChange={handleScanFileSelect}
                  className="hidden"
                />
                
                {scanPreview ? (
                  <div className="space-y-3">
                    <img src={scanPreview} alt="Preview" className="max-h-48 mx-auto rounded-lg shadow" />
                    <p className="text-sm text-purple-600 font-medium">{scanFile?.name}</p>
                  </div>
                ) : scanFile ? (
                  <div className="space-y-3">
                    <FileText className="w-16 h-16 text-purple-400 mx-auto" />
                    <p className="text-sm text-purple-600 font-medium">{scanFile.name}</p>
                    <p className="text-xs text-gray-500">PDF file selected</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <FileUp className="w-16 h-16 text-gray-300 mx-auto" />
                    <div>
                      <p className="font-medium text-gray-700">Drop voucher here or click to upload</p>
                      <p className="text-sm text-gray-500 mt-1">Supports JPG, PNG, WebP, PDF (max 10MB)</p>
                    </div>
                  </div>
                )}
              </div>

              {/* Selected Voucher Type Info */}
              <div className="mt-4 p-3 bg-gray-50 rounded-lg flex items-center gap-3">
                <TypeIcon className={`w-5 h-5 ${typeConfig.iconClass}`} />
                <span className="text-sm text-gray-600">
                  Scanning as: <span className="font-semibold">{typeConfig.label}</span>
                </span>
                <span className="text-xs text-gray-400 ml-auto">AI will auto-detect if different</span>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3 mt-6">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => { setShowScanModal(false); setScanFile(null); setScanPreview(null); }}
                >
                  Cancel
                </Button>
                <Button
                  className="flex-1 bg-purple-600 hover:bg-purple-700"
                  onClick={scanVoucher}
                  disabled={!scanFile || scanning}
                  data-testid="scan-submit-btn"
                >
                  {scanning ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Scanning...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-4 h-4 mr-2" />
                      Extract Data
                    </>
                  )}
                </Button>
              </div>

              {scanning && (
                <p className="text-center text-sm text-purple-600 mt-3">
                  AI is analyzing your voucher... This may take a few seconds.
                </p>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Form Section */}
        <div className="lg:col-span-2 space-y-6">
          {/* Passengers/Guests Section */}
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                  <Users className="w-5 h-5" /> 
                  {voucherType === 'hotel' ? 'Guests' : voucherType === 'flight' ? 'Passengers' : 'Participants'}
                </h3>
                <Button variant="outline" size="sm" onClick={addPassenger}>
                  <Plus className="w-4 h-4 mr-1" /> Add
                </Button>
              </div>

              <div className="space-y-3">
                {passengers.map((passenger, idx) => (
                  <div key={idx} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                    <span className="text-sm font-medium text-gray-500 w-6">{idx + 1}.</span>
                    <Input
                      placeholder={`${voucherType === 'hotel' ? 'Guest' : voucherType === 'flight' ? 'Passenger' : 'Participant'} Name *`}
                      value={passenger.name}
                      onChange={(e) => updatePassenger(idx, 'name', e.target.value)}
                      className="flex-1"
                    />
                    {voucherType === 'flight' && (
                      <Input
                        placeholder="Ticket Number (e.g., 098-2345678901)"
                        value={passenger.ticket_number || ''}
                        onChange={(e) => updatePassenger(idx, 'ticket_number', e.target.value)}
                        className="flex-1"
                        data-testid={`passenger-ticket-number-${idx}`}
                      />
                    )}
                    {!sameBarcode && (
                      <div className="flex items-center gap-2">
                        {passenger.barcode ? (
                          <div className="relative">
                            <img src={passenger.barcode} alt="Barcode" className="h-10 w-16 object-contain border rounded" />
                            <button
                              onClick={() => updatePassenger(idx, 'barcode', null)}
                              className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full p-0.5"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                        ) : (
                          <label className="cursor-pointer px-2 py-1 bg-white border rounded text-xs hover:bg-gray-50">
                            <QrCode className="w-4 h-4 inline mr-1" />
                            Barcode
                            <input type="file" className="hidden" accept="image/*" onChange={(e) => handlePassengerBarcodeUpload(e, idx)} />
                          </label>
                        )}
                      </div>
                    )}
                    {passengers.length > 1 && (
                      <button onClick={() => removePassenger(idx)} className="text-gray-400 hover:text-red-500">
                        <X className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                ))}
              </div>

              {/* Barcode Options */}
              {(voucherType === 'flight' || voucherType === 'activity') && passengers.length > 0 && (
                <div className="mt-4 p-4 bg-amber-50 rounded-lg border border-amber-200">
                  <div className="flex items-center gap-4 mb-3">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        checked={sameBarcode}
                        onChange={() => setSameBarcode(true)}
                        className="text-amber-600"
                      />
                      <span className="text-sm font-medium">Same barcode for all</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        checked={!sameBarcode}
                        onChange={() => setSameBarcode(false)}
                        className="text-amber-600"
                      />
                      <span className="text-sm font-medium">Different barcode per person</span>
                    </label>
                  </div>
                  
                  {sameBarcode && (
                    <div className="flex items-center gap-3">
                      <span className="text-sm text-gray-600">Shared Barcode:</span>
                      {sharedBarcode ? (
                        <div className="relative">
                          <img src={sharedBarcode} alt="Shared Barcode" className="h-12 w-20 object-contain border rounded bg-white" />
                          <button
                            onClick={() => setSharedBarcode(null)}
                            className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full p-0.5"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      ) : (
                        <label className="cursor-pointer px-3 py-2 bg-white border rounded text-sm hover:bg-gray-50 flex items-center gap-2">
                          <Upload className="w-4 h-4" />
                          Upload Barcode
                          <input type="file" className="hidden" accept="image/*" onChange={handleSharedBarcodeUpload} />
                        </label>
                      )}
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Main Form Fields */}
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center gap-3 mb-6">
                <div className={`p-3 rounded-xl ${typeConfig.badgeClass}`}>
                  <TypeIcon className={`w-6 h-6 ${typeConfig.iconClass}`} />
                </div>
                <div>
                  <h2 className="text-lg font-semibold">{typeConfig.label} Details</h2>
                  <p className="text-sm text-gray-500">Fill in the voucher information</p>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                {/* For flights with segments, render a different UI */}
                {voucherType === 'flight' ? (
                  <>
                    {/* Common flight fields (PNR, Airline, Class, Baggage) */}
                    {typeConfig.commonFields.map(field => (
                      <div key={field.name} className={field.type === 'textarea' ? 'md:col-span-2' : ''}>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          {field.label}
                          {field.required && <span className="text-red-500 ml-1">*</span>}
                        </label>
                        {field.type === 'select' ? (
                          <select
                            value={formData[field.name] || ''}
                            onChange={(e) => handleFieldChange(field.name, e.target.value)}
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white"
                          >
                            <option value="">Select...</option>
                            {field.options.map(opt => (
                              <option key={opt} value={opt}>{opt}</option>
                            ))}
                          </select>
                        ) : (
                          <Input
                            type={field.type}
                            value={formData[field.name] || ''}
                            onChange={(e) => handleFieldChange(field.name, e.target.value)}
                            placeholder={field.placeholder || ''}
                          />
                        )}
                      </div>
                    ))}

                    {/* Flight Segments Section */}
                    <div className="md:col-span-2 mt-4 pt-4 border-t">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                          <Plane className="w-5 h-5 text-indigo-600" />
                          Flight Segments
                          <span className="text-xs font-normal text-gray-500">
                            ({flightSegments.length} {flightSegments.length === 1 ? 'segment' : 'segments'})
                          </span>
                        </h3>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={addFlightSegment}
                          className="text-indigo-600 border-indigo-300 hover:bg-indigo-50"
                        >
                          <Plus className="w-4 h-4 mr-1" /> Add Connecting Flight
                        </Button>
                      </div>

                      {flightSegments.map((segment, segIndex) => (
                        <div key={segIndex} className="mb-6">
                          {/* Layover indicator between segments */}
                          {segIndex > 0 && (
                            <div className="flex items-center justify-center gap-3 my-4 py-3 bg-amber-50 border border-amber-200 rounded-lg">
                              <Clock className="w-4 h-4 text-amber-600" />
                              <span className="text-sm font-medium text-amber-700">
                                Layover at {flightSegments[segIndex - 1]?.arrival_city || 'Connection Point'}
                                {calculateLayover(flightSegments[segIndex - 1], segment) && (
                                  <span className="ml-2 px-2 py-0.5 bg-amber-100 rounded text-amber-800">
                                    {calculateLayover(flightSegments[segIndex - 1], segment)}
                                  </span>
                                )}
                              </span>
                            </div>
                          )}

                          <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
                            <div className="flex items-center justify-between mb-3">
                              <span className="font-semibold text-indigo-700 flex items-center gap-2">
                                <span className="w-6 h-6 bg-indigo-600 text-white rounded-full flex items-center justify-center text-xs">
                                  {segIndex + 1}
                                </span>
                                Flight Segment {segIndex + 1}
                              </span>
                              {flightSegments.length > 1 && (
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => removeFlightSegment(segIndex)}
                                  className="text-red-500 hover:text-red-700 hover:bg-red-50"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              )}
                            </div>

                            <div className="grid md:grid-cols-2 gap-3">
                              {typeConfig.segmentFields.map(field => (
                                <div key={`${segIndex}-${field.name}`}>
                                  <label className="block text-xs font-medium text-gray-600 mb-1">
                                    {field.label}
                                    {field.required && <span className="text-red-500 ml-1">*</span>}
                                  </label>
                                  <Input
                                    type={field.type}
                                    value={segment[field.name] || ''}
                                    onChange={(e) => handleSegmentChange(segIndex, field.name, e.target.value)}
                                    placeholder={field.placeholder || ''}
                                    className="text-sm"
                                  />
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      ))}

                      {flightSegments.length > 1 && (
                        <div className="text-sm text-gray-500 bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-start gap-2">
                          <Plane className="w-4 h-4 text-blue-500 mt-0.5" />
                          <span>
                            <strong>Connecting Flight:</strong> This voucher will show {flightSegments.length} flight segments 
                            with the same PNR ({formData.pnr || 'Not set'}) and layover times calculated automatically.
                          </span>
                        </div>
                      )}
                    </div>
                  </>
                ) : (
                  /* Regular fields for non-flight vouchers */
                  typeConfig.fields.map(field => (
                    <div key={field.name} className={field.type === 'textarea' ? 'md:col-span-2' : ''}>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {field.label}
                        {field.required && <span className="text-red-500 ml-1">*</span>}
                      </label>
                      {field.type === 'textarea' ? (
                        <textarea
                          value={formData[field.name] || ''}
                          onChange={(e) => handleFieldChange(field.name, e.target.value)}
                          placeholder={field.placeholder || ''}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm min-h-[80px]"
                        />
                      ) : field.type === 'select' ? (
                        <select
                          value={formData[field.name] || ''}
                          onChange={(e) => handleFieldChange(field.name, e.target.value)}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white"
                        >
                          <option value="">Select...</option>
                          {field.options.map(opt => (
                            <option key={opt} value={opt}>{opt}</option>
                          ))}
                        </select>
                      ) : (
                        <Input
                          type={field.type}
                          value={formData[field.name] || ''}
                          onChange={(e) => handleFieldChange(field.name, e.target.value)}
                          placeholder={field.placeholder || ''}
                        />
                      )}
                    </div>
                  ))
                )}
              </div>

              {/* Price Toggle */}
              <div className="mt-4 pt-4 border-t">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={showPrice}
                    onChange={(e) => setShowPrice(e.target.checked)}
                    className="w-4 h-4 text-amber-600 rounded"
                  />
                  <span className="text-sm font-medium text-gray-700">Show price/amount on voucher</span>
                </label>
              </div>
            </CardContent>
          </Card>

          {/* Images Section */}
          <Card>
            <CardContent className="p-6">
              <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Image className="w-5 h-5" /> Attachments & Images
              </h3>
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {voucherType === 'hotel' ? 'Hotel Image' : voucherType === 'flight' ? 'Airline Logo' : 'Activity Image'}
                  </label>
                  <label className="flex flex-col items-center justify-center h-24 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50">
                    <Upload className="w-6 h-6 text-gray-400 mb-1" />
                    <span className="text-xs text-gray-500">Upload</span>
                    <input type="file" className="hidden" accept="image/*" onChange={(e) => handleImageUpload(e, 'main', 'Main Image')} />
                  </label>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Image 2</label>
                  <label className="flex flex-col items-center justify-center h-24 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50">
                    <Upload className="w-6 h-6 text-gray-400 mb-1" />
                    <span className="text-xs text-gray-500">Upload</span>
                    <input type="file" className="hidden" accept="image/*" onChange={(e) => handleImageUpload(e, 'image2', 'Image 2')} />
                  </label>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Image 3</label>
                  <label className="flex flex-col items-center justify-center h-24 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50">
                    <Upload className="w-6 h-6 text-gray-400 mb-1" />
                    <span className="text-xs text-gray-500">Upload</span>
                    <input type="file" className="hidden" accept="image/*" onChange={(e) => handleImageUpload(e, 'image3', 'Image 3')} />
                  </label>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Image 4</label>
                  <label className="flex flex-col items-center justify-center h-24 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50">
                    <Upload className="w-6 h-6 text-gray-400 mb-1" />
                    <span className="text-xs text-gray-500">Upload</span>
                    <input type="file" className="hidden" accept="image/*" onChange={(e) => handleImageUpload(e, 'image4', 'Image 4')} />
                  </label>
                </div>
              </div>

              {/* Preview uploaded images */}
              {images.length > 0 && (
                <div className="flex flex-wrap gap-3 pt-3 border-t">
                  {images.map((img, idx) => (
                    <div key={idx} className="relative group">
                      <img src={img.preview} alt={img.label} className="w-24 h-20 object-cover rounded-lg border" />
                      <span className="absolute bottom-0 left-0 right-0 bg-black/60 text-white text-xs px-1 py-0.5 rounded-b-lg truncate">
                        {img.label}
                      </span>
                      <button
                        onClick={() => removeImage(idx)}
                        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Send Section */}
        <div className="space-y-6">
          <Card>
            <CardContent className="p-6">
              <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <FileText className="w-5 h-5" /> Voucher Actions
              </h3>

              <div className="space-y-4">
                {/* Preview & Download Buttons */}
                <div className="grid grid-cols-2 gap-2">
                  <Button
                    onClick={() => generateVoucherPDF('preview')}
                    disabled={generating}
                    variant="outline"
                    className="w-full"
                  >
                    {generating ? 'Generating...' : <><Eye className="w-4 h-4 mr-2" /> Preview</>}
                  </Button>
                  <Button
                    onClick={() => generateVoucherPDF('download')}
                    disabled={generating}
                    variant="outline"
                    className="w-full"
                  >
                    {generating ? 'Generating...' : <><FileText className="w-4 h-4 mr-2" /> Download</>}
                  </Button>
                </div>

                <div className="border-t pt-4">
                  <h4 className="font-medium text-gray-800 mb-3 flex items-center gap-2">
                    <Send className="w-4 h-4" /> Send via Email
                  </h4>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        <Mail className="w-4 h-4 inline mr-1" /> Recipient Email *
                      </label>
                      <Input
                        type="email"
                        value={sendTo}
                        onChange={(e) => setSendTo(e.target.value)}
                        placeholder="customer@email.com"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Link to Inquiry (optional)
                      </label>
                      <select
                        value={inquiryId}
                        onChange={(e) => setInquiryId(e.target.value)}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white"
                      >
                        <option value="">None</option>
                        {inquiries.map(inq => (
                          <option key={inq.id} value={inq.id}>
                            {inq.name} - {inq.destinations?.join(', ') || inq.message?.substring(0, 30)}
                          </option>
                        ))}
                      </select>
                    </div>

                    <Button
                      onClick={handleSendVoucher}
                      disabled={sending}
                      className="w-full bg-amber-600 hover:bg-amber-700"
                    >
                      {sending ? 'Sending...' : <><Send className="w-4 h-4 mr-2" /> Send Voucher</>}
                    </Button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Recent Vouchers */}
          <Card>
            <CardContent className="p-6">
              <h3 className="font-semibold text-gray-900 mb-4">Recent Vouchers</h3>
              {recentVouchers.length === 0 ? (
                <p className="text-sm text-gray-500 text-center py-4">No vouchers sent yet</p>
              ) : (
                <div className="space-y-3 max-h-80 overflow-y-auto">
                  {recentVouchers.map((v, i) => {
                    const config = VOUCHER_TYPES[v.voucher_type];
                    const Icon = config?.icon || FileText;
                    return (
                      <div key={i} className="flex items-center gap-3 p-2 bg-gray-50 rounded-lg">
                        <Icon className="w-5 h-5 text-gray-500" />
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-sm truncate">{v.send_to}</div>
                          <div className="text-xs text-gray-500">
                            {new Date(v.created_at).toLocaleDateString()}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Preview Modal */}
      {previewUrl && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="font-semibold text-lg">Voucher Preview</h3>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    const link = document.createElement('a');
                    link.href = previewUrl;
                    const firstName = passengers[0]?.name || 'voucher';
                    link.download = `${voucherType}_voucher_${firstName.replace(/\s+/g, '_')}.pdf`;
                    link.click();
                  }}
                >
                  <FileText className="w-4 h-4 mr-1" /> Download
                </Button>
                <button
                  onClick={closePreview}
                  className="p-2 hover:bg-gray-100 rounded-full"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-hidden">
              <iframe
                src={previewUrl}
                className="w-full h-full min-h-[70vh]"
                title="Voucher Preview"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminVouchers;
